import BottomSheet from '@components/BottomSheet'
import TokenPicker from '@components/TokenPicker'
import useBalances from '@plug-core/hooks/useBalances'
import { PlugToken } from '@plug-core/interfaces/common'
import { EarnChain, EarnProtocol, EarnVaultSortBy } from '@plug-core/interfaces/earn'
import { titleize } from '@plug-core/utils/titleize'
import { Avatar, Heading, ListGroup, ListRow, Text, cn } from '@ui'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiChevronDown, HiX } from 'react-icons/hi'

interface Props {
  chains: EarnChain[]
  protocols: EarnProtocol[]
  tokens: PlugToken[]
  chainId?: number
  protocol?: string
  asset?: string
  sortBy: EarnVaultSortBy
  onChainChange: (chainId: number | undefined) => void
  onProtocolChange: (protocol: string | undefined) => void
  onAssetChange: (asset: string | undefined) => void
  onSortChange: (sort: EarnVaultSortBy) => void
}

type Sheet = 'chain' | 'protocol' | 'asset' | 'sort' | null

const FilterButton = ({
  label,
  onClick,
  onClear,
  active,
}: {
  label: string
  onClick: () => void
  onClear?: () => void
  active: boolean
}) => {
  const showClear = active && !!onClear

  return (
    <div
      className={cn(
        'inline-flex shrink-0 items-center rounded-sm transition-colors duration-150',
        active ? 'bg-chip text-accent' : 'bg-surface-2 text-fg hover:bg-surface'
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex cursor-pointer items-center gap-1 border-0 bg-transparent py-1.5 pl-2.5 font-text text-[13px] font-semibold whitespace-nowrap text-inherit',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus',
          showClear ? 'pr-1' : 'pr-2.5'
        )}
      >
        {label}
        {!showClear && <HiChevronDown aria-hidden="true" size={14} />}
      </button>
      {showClear && (
        <button
          type="button"
          aria-label={label}
          onClick={onClear}
          className="flex cursor-pointer items-center border-0 bg-transparent py-1.5 pr-2.5 pl-0.5 text-inherit focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
        >
          <HiX aria-hidden="true" size={14} />
        </button>
      )}
    </div>
  )
}

const SelectableRow = ({
  label,
  logo,
  selected,
  onClick,
}: {
  label: string
  logo?: string
  selected: boolean
  onClick: () => void
}) => {
  const { t } = useTranslation()
  return (
    <ListRow
      onClick={onClick}
      selected={selected}
      leading={logo ? <Avatar src={logo} size={32} alt={label} /> : undefined}
      primary={label}
      trailing={
        <Text as="span" variant="caption" tone={selected ? 'success' : 'fg-2'}>
          {selected ? t('earn.selected') : t('earn.select')}
        </Text>
      }
    />
  )
}

const SheetHeading = ({ children }: { children: string }) => (
  <Heading level={4} align="center" className="mb-3">
    {children}
  </Heading>
)

export const VaultFilters: FC<Props> = ({
  chains,
  protocols,
  tokens,
  chainId,
  protocol,
  asset,
  sortBy,
  onChainChange,
  onProtocolChange,
  onAssetChange,
  onSortChange,
}) => {
  const { t } = useTranslation()
  const [sheet, setSheet] = useState<Sheet>(null)
  const { tokensToBalances } = useBalances()

  const close = () => setSheet(null)

  const selectedChain = chains.find((c) => c.chainId === chainId)
  const selectedProtocol = protocols.find((p) => p.name === protocol)

  return (
    <>
      <div className="flex shrink-0 gap-2 overflow-x-auto">
        <FilterButton
          label={selectedChain ? selectedChain.name : t('earn.allChains')}
          onClick={() => setSheet('chain')}
          onClear={() => onChainChange(undefined)}
          active={!!selectedChain}
        />
        <FilterButton
          label={selectedProtocol ? titleize(selectedProtocol.name) : t('earn.allProtocols')}
          onClick={() => setSheet('protocol')}
          onClear={() => onProtocolChange(undefined)}
          active={!!selectedProtocol}
        />
        <FilterButton
          label={asset ? asset : t('earn.allAssets')}
          onClick={() => setSheet('asset')}
          onClear={() => onAssetChange(undefined)}
          active={!!asset}
        />
        <FilterButton
          label={t('earn.sortLabel', { sort: sortBy === 'apy' ? 'APY' : 'TVL' })}
          onClick={() => setSheet('sort')}
          active
        />
      </div>

      <BottomSheet visible={sheet === 'chain'} onBackdropClick={close}>
        <div className="flex flex-col">
          <SheetHeading>{t('earn.filterByChain')}</SheetHeading>
          <ListGroup>
            {chains.map((chain) => (
              <SelectableRow
                key={chain.chainId}
                label={chain.name}
                selected={chainId === chain.chainId}
                onClick={() => {
                  onChainChange(chain.chainId)
                  close()
                }}
              />
            ))}
          </ListGroup>
        </div>
      </BottomSheet>

      <BottomSheet visible={sheet === 'protocol'} onBackdropClick={close}>
        <div className="flex flex-col">
          <SheetHeading>{t('earn.filterByProtocol')}</SheetHeading>
          <ListGroup>
            {protocols.map((p) => (
              <SelectableRow
                key={p.name}
                label={titleize(p.name)}
                logo={p.logoUri}
                selected={protocol === p.name}
                onClick={() => {
                  onProtocolChange(p.name)
                  close()
                }}
              />
            ))}
          </ListGroup>
        </div>
      </BottomSheet>

      <TokenPicker
        visible={sheet === 'asset'}
        title={t('earn.filterByAsset')}
        tokensForSelect={tokens}
        tokensToBalances={tokensToBalances}
        onClose={close}
        onTokenSelect={(token) => {
          if (token) {
            onAssetChange(token.symbol)
            if (token.evmChain) onChainChange(token.evmChain.chainId)
          }
          close()
        }}
      />

      <BottomSheet visible={sheet === 'sort'} onBackdropClick={close}>
        <div className="flex flex-col">
          <SheetHeading>{t('earn.sortBy')}</SheetHeading>
          <ListGroup>
            <SelectableRow
              label={t('earn.tvlHighest')}
              selected={sortBy === 'tvl'}
              onClick={() => {
                onSortChange('tvl')
                close()
              }}
            />
            <SelectableRow
              label={t('earn.apyHighest')}
              selected={sortBy === 'apy'}
              onClick={() => {
                onSortChange('apy')
                close()
              }}
            />
          </ListGroup>
        </div>
      </BottomSheet>
    </>
  )
}
